import { clsx } from "clsx";
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from "react";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightElement?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    hint,
    leftIcon,
    rightElement,
    className,
    id: externalId,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const id = externalId ?? generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  return (
    <div className={clsx("flex flex-col gap-1.5", className)}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-sub">
          {label}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-faint">
            {leftIcon}
          </div>
        )}

        <input
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error && errorId, hint && hintId].filter(Boolean).join(" ") ||
            undefined
          }
          className={clsx(
            "h-9 w-full rounded-md border bg-panel px-3 text-sm text-main",
            "placeholder:text-faint",
            "transition-colors duration-150",
            "focus:outline-none focus:ring-1",
            leftIcon && "pl-10",
            rightElement && "pr-10",
            error
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-edge hover:border-edge-hard focus:border-adara-500 focus:ring-adara-500",
          )}
          {...props}
        />

        {rightElement && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-1.5">
            {rightElement}
          </div>
        )}
      </div>

      {error && (
        <p id={errorId} className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {hint && !error && (
        <p id={hintId} className="text-sm text-dim">
          {hint}
        </p>
      )}
    </div>
  );
});
